import type { NuxtApp } from '#app';

const productCodeToInt = (item: any) => parseInt(item?.product.code, 10);

/**
 * This composable fetches and prepares data for the `product-listing` template used
 * in our catch-all and product-search pages.
 *
 * It is a bit problematic for the following reasons:
 *
 * 1) Used non-reactively like a regular util function (composable call on page reload, vs ref update on dep update)
 *
 * 2) We await `useAsyncData` calls inside the composable, which is recommended against
 * in the official docs (https://nuxt.com/docs/4.x/api/composables/use-async-data#usage).
 *
 * 3) We make complex modifications to the `page` object, which is hard to track and unclear
 * which references to the original object from the mainStore are being mutated this way.
 */
export const useProductListingTemplate = async (nuxtApp: NuxtApp, page: Record<string, any>) => {
  const data: Record<string, any> = {
    hideSidebar: ref(false),
  };

  const { $api, $isocodes, $i18n, $router, $log } = nuxtApp;
  const mainStore = useMainStore();
  const userStore = useUserStore();
  const route = unref($router.currentRoute);
  const languagePrefix = $isocodes[mainStore.language as keyof typeof $isocodes];
  const hideNavLinksFromCrawl = page.content?.navigation_settings?.hide_for_google;
  const startsWithUrl = route.path;
  const isLoggedIn = userStore.getIsAuthenticated || userStore.softLogin;
  const pageUrl = useGetPageUrl({ encode: true, fullPath: false });
  let buyAgainIds: Array<string> = [];

  const navigationSource = page.content.navigation_settings
    ? page.content.navigation_settings.navigation_source
    : 'navigation_nodes';

  const {
    solrQuery,
    sorting,
    currentPage,
    elevatedIds,
    searchIds,
    excludedFacets,
    facetSortOrder,
    isPredefinedTextSearch,
  } = getProductSearchArguments(
    route.query,
    page.content?.config?.filters,
    route.query.sort ? route.query.sort : page.content?.config?.sorting,
    page.content?.config?.elevateIds,
    page.content?.config?.searchIds,
    page.content?.config?.excludedFacets,
    page.content?.config?.facetSortOrder,
    page.content?.config?.excludedFilters,
  ) as any;

  // Load BuyAgainProducts for following request
  if (isLoggedIn && page.content.buy_again_settings) {
    // TODO: Should use `useAsyncData` in the future
    await userStore.loadBuyAgainProducts(true).then((response) => {
      buyAgainIds = response?.data?.buyAgainProducts?.map(productCodeToInt).join(',');
    });
  }

  const shouldMakeNodesRequest = navigationSource === 'navigation_nodes';

  const asyncDataNodes = useAsyncData(
    () => generateCacheKey({ pageUrl, shouldMakeNodesRequest }),
    async () => (shouldMakeNodesRequest ? $api.getNodes(unref(pageUrl)) : {}),
  );

  // Prepare products and nodes request params
  // Note: Currently no need to ref() here, this composable is called non-reactively, more like a regular util function
  const getProductsSearchParams = [
    solrQuery,
    sorting,
    currentPage,
    elevatedIds || null,
    searchIds || null,
    mainStore.language,
    excludedFacets,
    facetSortOrder,
    'FULL',
    null,
    startsWithUrl,
    buyAgainIds,
    mainStore.abTests,
  ] as const;

  const asyncDataProducts = useAsyncData(
    () => generateCacheKey(getProductsSearchParams),
    () => $api.hybris.product.search(...getProductsSearchParams),
    { transform: (val) => val.data },
  );

  const [nodesRequest, productRequest] = await Promise.all([asyncDataNodes, asyncDataProducts]);

  // 🚧 Ancient logic below. Not pretty, not proud — but it got the job done during the migration.
  // Proceed with caution, extra coffee and some snacks. Refactor at your own risk.
  const nodes = unref(nodesRequest.data) || {};
  const productList = unref(productRequest.data);

  productList.isPredefinedTextSearch = isPredefinedTextSearch;
  data.nodes = nodes;

  if (productList?.keywordRedirectUrl && productList.keywordRedirectUrl !== route.path) {
    if (productList.keywordRedirectUrl.includes('http')) {
      // Handle external redirects
      location.replace(productList.keywordRedirectUrl);
    } else {
      // Handle same url redirects
      mainStore.finishLoading();

      return {
        navigateTo: productList.keywordRedirectUrl,
      };
    }
  }

  const shouldUseSpellingSuggestion =
    (!productList?.products?.length || unref(mainStore.abTests).searchhub) &&
    productList?.spellingSuggestion?.suggestion &&
    route?.query?.text !== productList.spellingSuggestion.suggestion;

  if (shouldUseSpellingSuggestion) {
    // In case of same url redirects
    mainStore.finishLoading();

    // Use non-encoded query data to prevent double encoding. Redirected URL should not
    // include "%20" (space), "%2B" (plus) or "%253A" (double encoded space) as of now.
    $log.debug('Redirecting due to product search spelling suggestion...');

    return {
      navigateTo: {
        path: $i18n.t('url.search'),
        query: {
          text: decodeURI(productList.spellingSuggestion.suggestion.replace(/\+/g, ' ')),
          correctedText: route.query.text,
        },
      },
    };
  }

  // Check which navigation source should be displayed (defined in wp)
  switch (navigationSource) {
    case 'navigation_nodes':
      data.breadcrumbs = [
        {
          id: nodes.id,
          title: nodes.title,
          url: nodes.url,
        },
      ];

      nodes?.nodes?.forEach((node: any) => {
        let { url } = node;

        if (mainStore.isMultilang) {
          url = `/${languagePrefix}${node.url}`;
        }

        if (url === route.path) {
          data.breadcrumbs.push(node);
          return;
        }

        if (node.nodes) {
          node.nodes.forEach((child: any) => {
            let childUrl = child.url;

            if (mainStore.isMultilang) {
              childUrl = `/${languagePrefix}${childUrl}`;
            }
            if (childUrl === route.path) {
              data.breadcrumbs.push(node);
              data.breadcrumbs.push(child);
            }
          });
        }
      });
      break;

    case 'filter_categories': {
      const isSearchPage = route.path === $i18n.t('url.search');
      const searchUrl = route.query.text || (route.query?.q as string)?.split(':')[0] || null;

      if (isSearchPage) {
        data.breadcrumbs = [];
        // Push initial breadcrumb for the search page itself
        data.breadcrumbs.push({
          id: 'search',
          facetName: 'Kategorien',
          facetCode: 'category',
          facetValueName: $i18n.t('search.title'),
          truncateQuery: {
            url: `${$i18n.t('url.search')}?q=${searchUrl}`,
          },
        });
      } else {
        data.breadcrumbs = page.content.breadcrumbs;
      }

      // only display category breadcrumbs when filtering
      productList?.breadcrumbs?.forEach((node: any) => {
        if (node.truncateQuery) {
          node.truncateQuery.url = node.truncateQuery?.url.replace(/(\/search)/gi, '');
        }
        if (node.facetCode === 'category') {
          data.breadcrumbs.push({
            title: node.facetValueName,
            url: node.truncateQuery?.url ? node.truncateQuery?.url : '',
          });
        }
      });

      if (productList?.facets) {
        data.nodes = productList.facets.find((node: any) => node.code === 'category');
      }
      break;
    }

    case 'manual_list':
      data.nodes = {
        nodes: [],
      };

      page.content.navigation_settings.nodes.forEach((node: any) => {
        data.nodes.nodes.push(node.nodes);
      });

      data.nodes.nodes.forEach((node: any) => {
        if (node.url === route.path) {
          data.breadcrumbs = [
            {
              title: node.title,
              url: node.url,
            },
          ];
        }
      });

      if (!data.breadcrumbs?.length && page?.content?.breadcrumbs?.length) {
        data.breadcrumbs = page.content.breadcrumbs;
      }
      break;

    case 'none':
      data.hideSidebar = true;
      break;
  }

  // Store PLP breadcrumbs so that we can include them when building PDP breadcrumbs after going from PLP -> PDP
  mainStore.pdpBreadcrumbs.nodes = data.breadcrumbs || [];

  // Check if navigation links should not be crawled
  data.hideNavLinksFromCrawl = hideNavLinksFromCrawl;

  // Update datalayer store
  if (productList?.analytics) {
    const dataLayerStore = useDatalayerStore();
    dataLayerStore.filter = productList.analytics.filter;
    dataLayerStore.products = productList.analytics.products;
    dataLayerStore.search = productList.analytics.search;
  }

  data.query = { ...data.query, q: solrQuery };
  data.productList = productList;

  //  Add pagination links
  if (data.productList?.pagination) {
    const [path, params] = route.fullPath.split('?');
    const query = new URLSearchParams(params);

    // Remove all unexpected params
    const expectedParams = ['currentPage', 'q'];
    for (const key of Array.from(query.keys())) {
      if (!expectedParams.includes(key)) {
        query.delete(key);
      }
    }

    //  Prev link
    if (data.productList.pagination.currentPage > 0) {
      query.set('currentPage', data.productList.pagination.currentPage);

      if (currentPage === 1) {
        query.delete('currentPage');
      }

      const queryString = query.toString();
      data.productList.pagination.prevLink = `${path}${queryString ? `?${queryString}` : ''}`;
    }

    //  Next link
    if (data.productList.pagination.currentPage + 1 < data.productList.pagination.totalPages) {
      query.set('currentPage', data.productList.pagination.currentPage + 2);
      data.productList.pagination.nextLink = `${path}?${query.toString()}`;
    }
  }

  // If PLP filters has selected brand/s then get brand/s data
  const brands = productList.brands;
  data.relatedBrands = brands?.length ? brands : null;

  // Page is mutated inside this composable, so we need to write the changes back to the data object
  data.page = page;

  // Add seo page content
  data.filterPageActivated = false;
  return nuxtApp.runWithContext(() => useSeoPageContent({ data }));
};
